/**
 * Investment projection math for BestNeighbourhoods.ca.
 *
 * All calculations are original: standard Canadian mortgage amortisation
 * (semi-annual compounding by default), yearly projection tables, and a
 * clean separation of the four return components — cash flow, principal
 * paid down, appreciation, and net sale proceeds — so nothing is ever
 * double-counted.
 *
 * Units:
 *   - All money in CAD.
 *   - All rates in % (e.g. 4.5, not 0.045).
 *   - `holding_years` is the projection horizon in whole years.
 */

// ============================================================================
// Mortgage math (Canadian: semi-annual compounding for fixed, monthly for VRM)
// ============================================================================
const FREQ = { monthly: 12, biweekly: 26, weekly: 52 };

export function periodicRate(annualRatePct, rateType, freqPerYear) {
    const nominal = (annualRatePct || 0) / 100;
    if (rateType === 'variable') return nominal / freqPerYear;
    // Canadian fixed: semi-annual compounding by law
    const semi = Math.pow(1 + nominal / 2, 2) - 1;
    return Math.pow(1 + semi, 1 / freqPerYear) - 1;
}

export function mortgagePayment(principal, annualRatePct, amortYears, freqLabel, rateType) {
    const n = FREQ[freqLabel] || 12;
    const N = amortYears * n;
    const r = periodicRate(annualRatePct, rateType, n);
    if (principal <= 0 || N <= 0) return 0;
    if (r === 0) return principal / N;
    return (principal * r) / (1 - Math.pow(1 + r, -N));
}

/**
 * Amortise a mortgage over `years` and return per-year totals.
 * @returns Array<{ year, principal_paid, interest_paid, ending_balance, annual_payment }>
 */
export function amortiseYears(principal, annualRatePct, amortYears, freqLabel, rateType, years) {
    const n = FREQ[freqLabel] || 12;
    const r = periodicRate(annualRatePct, rateType, n);
    const pay = mortgagePayment(principal, annualRatePct, amortYears, freqLabel, rateType);
    const rows = [];
    let bal = principal;
    for (let y = 1; y <= years; y++) {
        let principal_paid = 0, interest_paid = 0;
        for (let k = 0; k < n; k++) {
            if (bal <= 0) break;
            const int = bal * r;
            const prin = Math.min(pay - int, bal);
            interest_paid += Math.max(int, 0);
            principal_paid += Math.max(prin, 0);
            bal -= prin;
        }
        rows.push({
            year: y,
            principal_paid,
            interest_paid,
            ending_balance: Math.max(bal, 0),
            annual_payment: pay * n,
        });
        if (bal <= 0) break;
    }
    return { schedule: rows, monthly_equivalent: pay * (n / 12) };
}

// ============================================================================
// Ontario land transfer tax (2026)
// Marginal brackets on the whole purchase price. Toronto MLTT roughly doubles it.
// ============================================================================
export function ontarioLTT(price, includeTorontoMLTT = false) {
    const brackets = [
        [55000, 0.005],
        [250000, 0.010],
        [400000, 0.015],
        [2000000, 0.020],
        [Infinity, 0.025],
    ];
    let tax = 0, prev = 0;
    for (const [cap, rate] of brackets) {
        if (price > prev) {
            tax += (Math.min(price, cap) - prev) * rate;
            prev = cap;
        } else break;
    }
    const provincial = Math.max(tax, 0);
    return includeTorontoMLTT ? provincial * 2 : provincial;
}

// ============================================================================
// The main projection calculator
// ============================================================================
/**
 * Everything the calculator returns. See `calculateProjection` for the contract.
 * @typedef {Object} ProjectionInputs
 * @property {'resale'|'preconstruction'} deal_type
 *
 * Property
 * @property {number} purchase_price
 * @property {number} [parking_income]              monthly
 * @property {number} [locker_income]               monthly
 * @property {number} [condo_fee]                   monthly
 *
 * Acquisition costs
 * @property {number} down_payment_pct
 * @property {number} [renovation_furnishing]
 * @property {boolean} include_toronto_mltt
 * @property {number} [legal_fees]
 * @property {number} [inspection_appraisal]
 * @property {number} [mortgage_insurance]          $ (already computed by caller if applicable)
 * @property {number} [financing_fees]
 * @property {number} [reserve_fund]
 * @property {number} [other_closing]
 *
 * Financing
 * @property {number} interest_rate
 * @property {number} amortization_years
 * @property {'monthly'|'biweekly'|'weekly'} payment_frequency
 * @property {'fixed'|'variable'} rate_type
 * @property {number} [stress_rate]                 optional stress-test rate
 *
 * Income
 * @property {number} monthly_rent
 * @property {number} [other_monthly_income]
 * @property {number} annual_rent_growth_pct
 * @property {number} vacancy_pct
 * @property {number} [bad_debt_pct]
 *
 * Operating
 * @property {number} annual_property_tax
 * @property {number} annual_insurance
 * @property {number} property_management_pct
 * @property {number} [utilities_annual]
 * @property {number} [repairs_maintenance_annual]
 * @property {number} [capex_reserve_annual]
 * @property {number} [licensing_annual]
 * @property {number} [misc_annual]
 * @property {number} expense_inflation_pct
 *
 * Projection
 * @property {number} appreciation_pct
 * @property {number} [selling_commission_pct]
 * @property {number} [selling_legal_fees]
 * @property {number} [mortgage_discharge_fee]
 * @property {number} [capital_improvements]        one-off during ownership
 * @property {number} holding_years
 *
 * Pre-construction (only when deal_type = 'preconstruction')
 * @property {number} [years_until_completion]
 * @property {number} [deposits_paid_before_closing]
 * @property {number} [expected_value_at_completion]
 * @property {number} [expected_rent_at_completion]
 * @property {number} [interim_occupancy_costs]     monthly during interim
 * @property {number} [development_charges]         at closing
 * @property {number} [assignment_costs]
 */

export function calculateProjection(input) {
    const i = input;
    const price = num(i.purchase_price);
    const downPct = num(i.down_payment_pct);
    const downCash = price * (downPct / 100);
    const mortgageAmount = Math.max(price - downCash, 0);

    // For pre-construction, some numbers apply only at completion
    const isPrec = i.deal_type === 'preconstruction';
    const yearsToCompletion = isPrec ? Math.max(1, num(i.years_until_completion) || 1) : 0;
    const closingPrice = isPrec ? num(i.expected_value_at_completion) || price : price;
    const rentBase = isPrec ? num(i.expected_rent_at_completion) || num(i.monthly_rent) : num(i.monthly_rent);

    const ltt = ontarioLTT(price, !!i.include_toronto_mltt);
    const totalClosing =
        ltt +
        num(i.legal_fees) +
        num(i.inspection_appraisal) +
        num(i.mortgage_insurance) +
        num(i.financing_fees) +
        num(i.reserve_fund) +
        num(i.other_closing) +
        (isPrec ? num(i.development_charges) + num(i.assignment_costs) : 0);

    const totalInitialCash =
        downCash +
        num(i.renovation_furnishing) +
        totalClosing +
        (isPrec ? num(i.deposits_paid_before_closing) : 0);

    // Mortgage stream (starts at closing/completion)
    const amort = amortiseYears(
        mortgageAmount,
        num(i.interest_rate),
        num(i.amortization_years) || 25,
        i.payment_frequency || 'monthly',
        i.rate_type || 'fixed',
        num(i.holding_years) || 1,
    );
    const monthlyPayment = amort.monthly_equivalent;
    const annualDebtService = monthlyPayment * 12;

    // Stress-test payment
    let stressed_annual_debt_service = null;
    if (num(i.stress_rate) > 0) {
        const stressAmort = amortiseYears(mortgageAmount, num(i.stress_rate), num(i.amortization_years) || 25, i.payment_frequency || 'monthly', i.rate_type || 'fixed', 1);
        stressed_annual_debt_service = stressAmort.monthly_equivalent * 12;
    }

    // Year-by-year projection
    const holding = Math.max(1, Math.floor(num(i.holding_years) || 1));
    const table = [];
    let cumulative_cash_flow = 0;

    for (let y = 1; y <= holding; y++) {
        // rent grown from base
        const yearMonthlyRent = rentBase * Math.pow(1 + num(i.annual_rent_growth_pct) / 100, y - 1);
        const otherMonthly = num(i.other_monthly_income) + num(i.parking_income) + num(i.locker_income);
        const gross_annual_income = (yearMonthlyRent + otherMonthly) * 12;
        const vacancy_loss = gross_annual_income * (num(i.vacancy_pct) / 100);
        const bad_debt = gross_annual_income * (num(i.bad_debt_pct) / 100);
        const effective_income = gross_annual_income - vacancy_loss - bad_debt;

        // operating expenses grown by inflation
        const infl = Math.pow(1 + num(i.expense_inflation_pct) / 100, y - 1);
        const mgmt = effective_income * (num(i.property_management_pct) / 100);
        const opex_before_mgmt =
            num(i.annual_property_tax) * infl +
            num(i.annual_insurance) * infl +
            num(i.condo_fee) * 12 * infl +
            num(i.utilities_annual) * infl +
            num(i.repairs_maintenance_annual) * infl +
            num(i.capex_reserve_annual) * infl +
            num(i.licensing_annual) * infl +
            num(i.misc_annual) * infl;
        const total_opex = opex_before_mgmt + mgmt;
        const noi = effective_income - total_opex;

        const debtRow = amort.schedule[y - 1] || { annual_payment: annualDebtService, principal_paid: 0, interest_paid: 0, ending_balance: mortgageAmount };
        const net_cash_flow = noi - debtRow.annual_payment;
        cumulative_cash_flow += net_cash_flow;

        // Property value: from closingPrice, appreciated
        const property_value = closingPrice * Math.pow(1 + num(i.appreciation_pct) / 100, y);
        const appreciation = property_value - closingPrice;
        const remaining_mortgage = debtRow.ending_balance;
        const principal_paid_cum = mortgageAmount - remaining_mortgage;
        const equity = property_value - remaining_mortgage;

        // Net sale proceeds at end of year y
        const selling_commission = property_value * (num(i.selling_commission_pct) / 100);
        const total_selling_costs = selling_commission + num(i.selling_legal_fees) + num(i.mortgage_discharge_fee);
        const net_sale_proceeds = property_value - remaining_mortgage - total_selling_costs;

        // Total profit at end of year y (if sold): cash flow + net sale proceeds - initial cash - capex additions
        const total_profit = cumulative_cash_flow + net_sale_proceeds - totalInitialCash - num(i.capital_improvements);
        const roic = totalInitialCash > 0 ? (total_profit / totalInitialCash) * 100 : 0;
        const annualised = totalInitialCash > 0 && y > 0
            ? (Math.pow((totalInitialCash + total_profit) / totalInitialCash, 1 / y) - 1) * 100
            : 0;

        table.push({
            year: y,
            monthly_rent: yearMonthlyRent,
            gross_annual_income,
            vacancy_loss,
            effective_income,
            total_opex,
            noi,
            annual_debt_service: debtRow.annual_payment,
            interest_paid: debtRow.interest_paid,
            principal_paid_year: debtRow.principal_paid,
            principal_paid_cum,
            net_cash_flow,
            cumulative_cash_flow,
            property_value,
            appreciation,
            remaining_mortgage,
            equity,
            selling_commission,
            total_selling_costs,
            net_sale_proceeds,
            total_profit,
            roic,
            annualised,
        });
    }

    // Year-1 headline metrics
    const y1 = table[0];
    const gross_yield_pct = closingPrice > 0 ? (y1.gross_annual_income / closingPrice) * 100 : 0;
    const net_yield_pct = closingPrice > 0 ? (y1.noi / closingPrice) * 100 : 0;
    const cap_rate_pct = closingPrice > 0 ? (y1.noi / closingPrice) * 100 : 0;
    const coc_pct = totalInitialCash > 0 ? (y1.net_cash_flow / totalInitialCash) * 100 : 0;
    const dscr = annualDebtService > 0 ? y1.noi / annualDebtService : Infinity;
    const total_annual_opex = y1.total_opex;
    const total_gross = y1.gross_annual_income;
    const break_even_occ_pct = total_gross > 0 ? ((total_annual_opex + annualDebtService) / total_gross) * 100 : 0;

    return {
        // Inputs echoed
        purchase_price: price,
        closing_price: closingPrice,
        mortgage_amount: mortgageAmount,
        down_payment: downCash,
        holding_years: holding,

        // Acquisition
        land_transfer_tax: ltt,
        total_closing_costs: totalClosing,
        total_initial_cash: totalInitialCash,

        // Financing
        monthly_mortgage_payment: monthlyPayment,
        annual_debt_service: annualDebtService,
        stressed_annual_debt_service,

        // Year 1
        monthly_rent_y1: rentBase,
        gross_annual_income_y1: y1.gross_annual_income,
        effective_income_y1: y1.effective_income,
        total_opex_y1: y1.total_opex,
        noi_y1: y1.noi,
        monthly_cash_flow_y1: y1.net_cash_flow / 12,
        annual_cash_flow_y1: y1.net_cash_flow,
        gross_yield_pct,
        net_yield_pct,
        cap_rate_pct,
        coc_pct,
        dscr,
        break_even_occ_pct,

        // Projection table
        table,

        // At the end of the holding period
        end: table[table.length - 1],
    };
}

function num(x) {
    const n = Number(x);
    return isFinite(n) && !isNaN(n) ? n : 0;
}

// ============================================================================
// Reality-check flags
// ============================================================================
export function realityChecks(p, inputs) {
    const flags = [];
    if (p.annual_cash_flow_y1 < 0) flags.push({ level: 'warn', tag: 'Cash-flow constrained', body: 'Year-1 cash flow is negative. The property will need out-of-pocket support until rent growth catches operating costs.' });
    if (p.dscr < 1) flags.push({ level: 'warn', tag: 'DSCR below 1.0', body: `Net operating income does not cover debt service in year 1 (DSCR ${p.dscr.toFixed(2)}). Most lenders require ≥1.10-1.20.` });
    if ((inputs.appreciation_pct || 0) > 5) flags.push({ level: 'info', tag: 'Aggressive appreciation assumption', body: `${inputs.appreciation_pct}% annual appreciation is above the Southern Ontario 20-year average of ~3.5-4.5%. Stress-test at 3%.` });
    if ((inputs.annual_rent_growth_pct || 0) > 5) flags.push({ level: 'info', tag: 'Aggressive rent-growth assumption', body: `${inputs.annual_rent_growth_pct}% annual rent growth is above CMHC's long-run 3-4% band. Stress-test at 2.5%.` });
    if ((inputs.vacancy_pct || 0) < 4) flags.push({ level: 'info', tag: 'Low vacancy allowance', body: 'Vacancy below 4% is optimistic for single-family. Model 5-8% to be defensible.' });
    if ((inputs.repairs_maintenance_annual || 0) + (inputs.capex_reserve_annual || 0) < p.closing_price * 0.005) {
        flags.push({ level: 'info', tag: 'Insufficient repair reserve', body: 'Combined repairs and capex reserve is below 0.5% of purchase price per year — thin for a long hold.' });
    }
    if ((inputs.condo_fee || 0) * 12 > p.gross_annual_income_y1 * 0.20) {
        flags.push({ level: 'info', tag: 'High condo-fee burden', body: 'Condo fees exceed 20% of gross rent — a significant drag on cash flow and resale liquidity.' });
    }
    const equityShare = p.total_initial_cash / Math.max(p.purchase_price, 1);
    if (equityShare < 0.20) flags.push({ level: 'info', tag: 'Limited cash reserves', body: 'Down payment plus closing is under 20% of price — leverage is high, sensitivity to rate moves is elevated.' });
    if (p.stressed_annual_debt_service && p.stressed_annual_debt_service > p.noi_y1) {
        flags.push({ level: 'warn', tag: 'High interest-rate sensitivity', body: `At the stress rate (${inputs.stress_rate}%), debt service exceeds year-1 NOI. Refinance risk should be planned for.` });
    }
    // Appreciation-dependent test: >70% of profit comes from appreciation vs cash flow + paydown
    const end = p.end;
    if (end && end.total_profit > 0) {
        const shareApp = end.appreciation / Math.max(end.total_profit, 1);
        if (shareApp > 0.70) flags.push({ level: 'info', tag: 'Appreciation dependent', body: 'More than 70% of projected return comes from price appreciation. Underlying cash flow is thin — cycle risk is real.' });
    }
    return flags;
}

// ============================================================================
// Regional presets for Southern Ontario (2026 directional estimates only)
// ============================================================================
export const REGIONAL_PRESETS = {
    'durham': {
        label: 'Durham Region',
        property_tax_pct_of_price: 1.25,
        median_rent_2br: 2450,
        vacancy_pct: 2.1,
        condo_fee_range: [420, 720],
        insurance_annual: 1650,
    },
    'hamilton': {
        label: 'Hamilton',
        property_tax_pct_of_price: 1.30,
        median_rent_2br: 2295,
        vacancy_pct: 1.6,
        condo_fee_range: [400, 680],
        insurance_annual: 1580,
    },
    'halton': {
        label: 'Halton Region',
        property_tax_pct_of_price: 0.78,
        median_rent_2br: 2825,
        vacancy_pct: 1.4,
        condo_fee_range: [480, 900],
        insurance_annual: 1750,
    },
    'brantford': {
        label: 'Brantford',
        property_tax_pct_of_price: 1.45,
        median_rent_2br: 1975,
        vacancy_pct: 2.4,
        condo_fee_range: [360, 620],
        insurance_annual: 1450,
    },
    'brant-county': {
        label: 'Brant County',
        property_tax_pct_of_price: 1.15,
        median_rent_2br: 1875,
        vacancy_pct: 3.1,
        condo_fee_range: [350, 580],
        insurance_annual: 1400,
    },
    'niagara': {
        label: 'Niagara Region',
        property_tax_pct_of_price: 1.35,
        median_rent_2br: 2095,
        vacancy_pct: 2.8,
        condo_fee_range: [380, 680],
        insurance_annual: 1520,
    },
};

// Band helpers for GHL tagging
export function bandCapRate(pct) {
    if (pct < 3) return 'under-3';
    if (pct < 4) return '3-4';
    if (pct < 5) return '4-5';
    if (pct < 6) return '5-6';
    return '6-plus';
}
export function bandCoC(pct) {
    if (pct < 0) return 'negative';
    if (pct < 4) return '0-4';
    if (pct < 8) return '4-8';
    if (pct < 12) return '8-12';
    return '12-plus';
}
export function bandDSCR(v) {
    if (v < 1) return 'under-1';
    if (v < 1.2) return '1-1.2';
    if (v < 1.5) return '1.2-1.5';
    return '1.5-plus';
}
